import React, { useState, useEffect, useRef } from "react";
import { Modal, Icon, Spin } from "antd";
import DASHBOARD_API from "../../../apis/dashboard.api";

export const InviteChatModal = ({
  open,
  handleClose,
  currentUser,
  handleInvite,
}) => {
  const [formData, setFormData] = useState(null);
  const [error, setError] = useState(null);
  const [userSearchQuery, setUserSearchQuery] = useState("");
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const limit = 20;
  const searchTimeoutRef = useRef(null);

  const isValidEmail = (email) => {
    return /\S+@\S+\.\S+/.test(email);
  };

  const fetchUsers = async (searchParams, pageNum) => {
    try {
      setLoading(true);
      const data = await DASHBOARD_API.fetchUserList(pageNum, limit, searchParams);
      if (data && data.data) {
        let fetchedUsers = data.data;
        if (currentUser && currentUser.id) {
          fetchedUsers = fetchedUsers.filter((user) => user.id !== currentUser.id);
        }
        
        if (pageNum === 1) {
          setUsers(fetchedUsers);
        } else {
          setUsers((prev) => [...prev, ...fetchedUsers]);
        }

        if (data.data.length < limit) {
          setHasMore(false);
        } else {
          setHasMore(true);
        }
      } else {
        if (pageNum === 1) setUsers([]);
        setHasMore(false);
      }
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchUsers(userSearchQuery, 1);
    }
    // Cleanup timeout on unmount or modal close
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [open]);

  const handleChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value,
    });
    setUserSearchQuery(value);

    setPage(1);
    setHasMore(true);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      fetchUsers(value, 1);
    }, 500);
  };

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchUsers(userSearchQuery, nextPage);
  };

  const handleSave = async (user) => {
    handleInvite(user);
  };

  const handleScroll = (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 5) {
      if (!loading && hasMore) {
        loadMore();
      }
    }
  };

  return (
    <div>
      {" "}
      <Modal
        visible={open}
        footer={null}
        onOk={handleClose}
        onCancel={handleClose}
        closable={false}
        centered
      >
        <div className="chat-invite-modal">
          <div className="chat-invite-modal__header">
            <div>
              <h3 className="chat-invite-modal__title">Invite User to Chat</h3>
              <p className="chat-invite-modal__subtitle">
                Search by email or name and choose the person you want to invite.
              </p>
            </div>
            <button
              type="button"
              className="chat-invite-modal__close"
              onClick={() => {
                setUserSearchQuery("");
                handleClose();
              }}
            >
              <Icon type="close" />
            </button>
          </div>

          <div className="chat-invite-modal__field">
            <h3 className="inputTitle">
              Enter Email <span style={{ color: "red" }}>*</span>
            </h3>
            <div className="chat-invite-modal__input-shell">
          <input
            className="roleInput chat-invite-modal__input"
            placeholder="Search by email or name"
            name="email"
            type="email"
            value={formData && formData.email}
            id="email"
            onChange={(e) => {
              e.preventDefault();
              e.persist();
              if (!isValidEmail(e.target.value)) {
                if (e.target.value === "") {
                  setError(null);
                } else {
                  setError("Email is invalid");
                }
              } else {
                setError(null);
              }
              handleChange(e.target.name, e.target.value);
            }}
          />
            </div>
          {error && (
            <span style={{ color: "red", fontSize: "12px" }}>{error}</span>
          )}
          </div>

          <div className="chat-invite-modal__results" onScroll={handleScroll} style={{ overflowY: "auto", maxHeight: "300px" }}>
            {loading && page === 1 ? (
              <div style={{ textAlign: "center", padding: "20px" }}>
                <Spin indicator={<Icon type="loading" style={{ fontSize: 24 }} spin />} />
              </div>
            ) : users && users.length > 0 ? (
              <>
                {users.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => {
                      handleSave(item);
                    }}
                    className="chat-invite-modal__result"
                  >
                    <div className="contact-chat-logo">
                      {item.name
                        ? item.name
                            .split(" ")
                            .map((part) => part[0])
                            .join("")
                        : "N/A"}
                    </div>
                    <div className="chat-invite-modal__result-content">
                      <span className="chat-invite-modal__result-name">
                        {item.name}
                      </span>
                      <span className="chat-invite-modal__result-email">
                        {item.email}
                      </span>
                    </div>
                    <span className="chat-invite-modal__result-cta">Invite</span>
                  </button>
                ))}
                
                {hasMore && (
                  <div style={{ textAlign: "center", marginTop: "10px", marginBottom: "10px" }}>
                    <button
                      type="button"
                      className="savebutton"
                      onClick={loadMore}
                      disabled={loading}
                      style={{ fontSize: "12px", padding: "4px 12px" }}
                    >
                      {loading ? "Loading..." : "Load More"}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="chat-invite-modal__empty">
                <div className="chat-invite-modal__empty-title">
                  {userSearchQuery ? "No user found" : "Start by searching"}
                </div>
                <div className="chat-invite-modal__empty-text">
                  {userSearchQuery
                    ? "Try another email address or search using the user's name."
                    : "Enter an email address to find a user you can invite to chat."}
                </div>
              </div>
            )}
          </div>

          <div className="buttonsMain" style={{ justifyContent: "center", marginTop: "16px" }}>
            <button
              className="savebutton"
              onClick={() => {
                setUserSearchQuery("");
                handleClose();
              }}
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
